import { useState } from "react";
import { useUsageLimit } from "../../../customhooks/useUsageLimit.js";
import { useAuth } from "../../Login/AuthContext.js";
import DifficultyDropdown from "../../Difficulty/difficulty.js";
import { CircularProgress } from "@mui/material";

import numbersIcon from "../../../assets/numbers.png";
import glitterIcon from "../../../assets/glitter.svg"
import "./OnetoOneConversion.css";
import PromptPreview from "../../CustomPrompt/PromptPreview.jsx";

// Toggle which parts of the prompt are shown in the preview box
const PROMPT_PARTS = {
  mainQuery: true, // "Generate exactly 30 unique {difficulty}-level questions for: {conversions}"
};

function buildPromptPreview(difficulty, conversions) {
  if (!PROMPT_PARTS.mainQuery) return '';
  const diffStr = difficulty?.toLowerCase() || '[difficulty not set]';
  const convStr = conversions.length > 0 ? conversions.join(', ') : '[no conversions selected]';
  return `Generate exactly 30 unique ${diffStr}-level questions for the conversion type: ${convStr}.`;
}

const Conversiontemplate = ({ generateContentAndPdf, loading }) => {
  const [selectedConversions, setSelectedConversions] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);

  const promptPreview = buildPromptPreview(selectedDifficulty, selectedConversions);

  const { isAuthenticated, aiAllowance } = useAuth();
  const { remainingUses, isDisabled, incrementUsage } = useUsageLimit(
    "globalUsage",
    5,
    undefined,
    isAuthenticated
  );

  const [errors, setErrors] = useState({
    firstNumError: "",
    difficultyError: ""
  });

  const allOptions = [
    'Fraction to Decimal',
    'Fraction to Percentage',
    'Decimal to Fraction',
    'Decimal to Percentage',
    'Percentage to Fraction',
    'Percentage to Decimal'
  ];

  const handleChange = (e) => {
    const value = e.target.value;
    if (value && !selectedConversions.includes(value)) {
      setSelectedConversions((prev) => {
        const updated = [...prev, value];
        if (updated.length > 0) {
          setErrors((prevErrors) => ({ ...prevErrors, firstNumError: "" }));
        }
        return updated;
      });
    }
  };

  const removeConversion = (conversion) => {
    const updated = selectedConversions.filter((item) => item !== conversion);
    setSelectedConversions(updated);
    if (updated.length > 0) {
      setErrors((prevErrors) => ({ ...prevErrors, firstNumError: "" }));
    }
  };

  const availableOptions = allOptions.filter((opt) => !selectedConversions.includes(opt));

  const submitHandler = (e) => {
    e.preventDefault();
    if (aiAllowance?.usage?.remaining === 0) return;

    const newErrors = {};
    let hasError = false;

    if (selectedConversions.length === 0) {
      newErrors.firstNumError = "Please select at least one conversion type.";
      hasError = true;
    }
    if (!selectedDifficulty) {
      newErrors.difficultyError = "Please select a difficulty level.";
      hasError = true;
    }

    if (!isAuthenticated && isDisabled) {
      alert("You have reached the maximum number of uses. Please sign up for free credits! 👉🏻");
      return;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const statusText = `Generate exactly 30 unique ${selectedDifficulty?.toLowerCase()}-level questions for the conversion type: ${selectedConversions.join(", ")}.`;

    if (!isAuthenticated) incrementUsage();
    generateContentAndPdf(statusText);
  };

  return (
    <div className="column-questions">

      <label className="form-label">
        Only <strong>30 questions</strong> per worksheet for this template
      </label>

         <PromptPreview text={promptPreview} />

      <div className="oto-body">
        <div className="oto-selectors-row">
          <div className="oto-selector-group">
            <label className="form-label">
              <img src={numbersIcon} alt="info" style={{ width: "16px", height: "16px" }} />
              Select the types of <strong>conversions</strong> you want to include:
            </label>
            <select className="conversion-selection" onChange={handleChange} value="">
              <option value="" disabled>Select a conversion</option>
              {availableOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {errors.firstNumError && <p className="error">{errors.firstNumError}</p>}
          </div>

          <div className="oto-selector-group">
            <DifficultyDropdown onClick={setSelectedDifficulty} selectedDifficulty={selectedDifficulty} />
            {errors.difficultyError && <p className="error">{errors.difficultyError}</p>}
          </div>

          <button
            className="generate-button"
            data-gtm-event="track-click"
            gtm-data-label="generated-questions-conversions-temp"
            onClick={submitHandler}
            disabled={loading || aiAllowance?.usage?.remaining === 0}
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" style={{ marginRight: '8px' }} />
            ) : (
              <img src={glitterIcon} alt="addIcon" style={{ width: '16px', height: '16px', marginRight: '5px' }} />
            )}
            {loading ? 'Generating...' : isAuthenticated ? 'Generate Questions' : `Generate (${remainingUses} remaining)`}
          </button>
        </div>

        {selectedConversions.length > 0 && (
          <div className="selected-conversion-box">
            <div className="selected-header">
              <h4 className="conversion-heading">Selected Conversions:</h4>
              <button className="clear-all-btn" onClick={() => setSelectedConversions([])}>
                Clear All
              </button>
            </div>
            <div className="selected-tags">
              {selectedConversions.map((conversion) => (
                <span key={conversion} className="conversion-tag">
                  {conversion}
                  <button className="remove-btn" onClick={() => removeConversion(conversion)} title="Remove">
                    &times;
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

   
    </div>
  );
};

export default Conversiontemplate;
